export type AppTheme = 'light' | 'dark';

export const DEFAULT_APP_THEME: AppTheme = 'light';

const APP_THEMES: AppTheme[] = ['light', 'dark'];
const BROWSER_AUTO_DARK_ATTRIBUTE = 'browserAutoDark';

export function normalizeAppTheme(value: string | null | undefined): AppTheme {
  return APP_THEMES.includes(value as AppTheme) ? value as AppTheme : DEFAULT_APP_THEME;
}

export function readSystemThemePreference(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;
}

export function detectBrowserAutoDarkTheme(): boolean {
  if (typeof document === 'undefined' || !document.body || typeof window.getComputedStyle !== 'function') {
    return false;
  }

  const cachedDetection = document.documentElement.dataset[BROWSER_AUTO_DARK_ATTRIBUTE];
  if (cachedDetection) {
    return cachedDetection === 'true';
  }

  const probe = document.createElement('div');
  probe.dataset.palenAutoDarkProbe = 'true';
  probe.style.display = 'none';
  probe.style.backgroundColor = 'canvas';
  probe.style.colorScheme = 'light';
  document.body.appendChild(probe);

  const backgroundColor = window.getComputedStyle(probe).backgroundColor || '';
  probe.remove();

  const rgb = backgroundColor.match(/[\d.]+/g)?.map(Number);
  const isAutoDark = Boolean(
    rgb
    && rgb.length >= 3
    && (rgb.length < 4 || rgb[3] > 0)
    && (rgb[0] !== 255 || rgb[1] !== 255 || rgb[2] !== 255),
  );
  document.documentElement.dataset[BROWSER_AUTO_DARK_ATTRIBUTE] = String(isAutoDark);
  return isAutoDark;
}

export function resolveAppTheme(
  preference: string | null | undefined,
  prefersDark = readSystemThemePreference()?.matches ?? false,
  browserAutoDark = detectBrowserAutoDarkTheme(),
): AppTheme {
  return APP_THEMES.includes(preference as AppTheme)
    ? preference as AppTheme
    : prefersDark || browserAutoDark ? 'dark' : DEFAULT_APP_THEME;
}

export function applyAppTheme(theme: AppTheme): void {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme === 'light' ? 'only light' : 'dark';
  updateColorSchemeMeta(theme);
  updateThemeColorMeta(theme);
  window.dispatchEvent(new CustomEvent('app-theme-change', { detail: { theme } }));
}

function updateColorSchemeMeta(theme: AppTheme): void {
  const meta = getOrCreateMeta('color-scheme');
  meta.setAttribute('content', theme === 'light' ? 'light only' : 'dark');
}

function updateThemeColorMeta(theme: AppTheme): void {
  const themeColor = theme === 'dark' ? '#020617' : '#edf8ff';
  const meta = getOrCreateMeta('theme-color');
  meta.setAttribute('content', themeColor);
}

function getOrCreateMeta(name: string): HTMLMetaElement {
  const existingMeta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (existingMeta) {
    return existingMeta;
  }

  const meta = document.createElement('meta');
  meta.name = name;
  document.head.appendChild(meta);
  return meta;
}
